import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { LearningRouterProps } from '@pages/learning/LearningRouter.types';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

const LearningRouter = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const getToken = useGetToken();

  const [RemoteRouter, setRemoteRouter] =
    React.useState<React.ComponentType<LearningRouterProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
      const loadRouter = async (): Promise<void> => {
        try {
          const { default: Router } =
            await import('learning_mfe/LearningRouter');
          setRemoteRouter(() => Router);
        } catch (error) {
          console.error('Error loading LearningRouter:', error);
          setLoadError(error as Error);
        }
      };

      loadRouter();
    }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteRouter) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteRouter
        msalInstance={instance}
        user={authData}
        permissions={permissions}
        getToken={getToken}
      />
    </React.Suspense>
  );
});

LearningRouter.displayName = 'LearningRouter';

export { LearningRouter };
